import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import socket from "utils/Socket";
import storage from "utils/Storage";
import { decode, encode, sendNotfication, Event, wait, API } from 'utils/Helper';
import C from 'utils/Constant';
import { useVerifyReferralMutation } from 'hooks/useAuth';
import { handleGoogleLogin, GoogleLoginButton } from './GoogleLoginButton';
import ReCAPTCHA from "react-google-recaptcha";
import { Eye, EyeOff } from 'lucide-react';

const SocialLoginContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 15px;
`;

const StyledModal = styled(Modal)`
  .modal-dialog {
    margin: auto !important;
    @media(min-width:640px){
      max-width: 560px !important; /* Stake modal max width */
    }
  }
  .modal-content {
    background-color: #1A2C38; /* Stake card background */
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: white;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
    
    /* Scroller settings exactly like Stake */
    height: fit-content !important;
    max-height: 85vh !important;
    overflow-y: auto !important;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #2F4553;
      border-radius: 4px;
    }
  }

  /* Force equal padding on top and bottom of the modal container */
  &.modal {
    padding-top: 5vh !important;
    padding-bottom: 5vh !important;
  }

    @media(max-width:340px){
      margin: 5px !important;
    }
  }
`;

const ModalHeader = styled.div`
  background-color transparent;
  padding-left:15px;
  padding-right:15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #9CA3AF;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  line-height: 1;

  &:hover {
    color: white;
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: 32px;
  position: relative;
  @media(max-width:400px){
    padding: 24px;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px; /* Stake spacing */
`;

const StyledInput = styled.input`
  background-color: #0F212E; /* Stake input background */
  border: 1px solid #2F4553;
  border-radius: 6px;
  color: white;
  padding: 0 16px;
  height: 48px;
  font-size: 14px;
  width: 100%;
  transition: all 0.2s ease-in-out;

  &::placeholder {
    color: #557086;
  }
  &:focus {
    outline: none;
    border-color: #1fdf71;
    background-color: #0F212E;
  }
  
  /* Fix browser autofill */
  &:-webkit-autofill,
  &:-webkit-autofill:hover, 
  &:-webkit-autofill:focus, 
  &:-webkit-autofill:active{
    -webkit-box-shadow: 0 0 0 30px #0F212E inset !important;
    -webkit-text-fill-color: white !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #B1BAD3;
  margin-bottom: 8px;
`;

const FieldContainer = styled.div`
  width: 100%;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const Labeltext = styled.div`
  color: #696F79;
  font-size: 14px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const Button = styled.button`
   padding: 10px 20px;
  border-radius: 7px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
`;

const SignUpButton = styled.button`
  background-image: linear-gradient(90deg, #1fdf71, #84e056);
  box-shadow: rgba(31, 223, 113, 0.4) 0px 4px 14px 0px, rgba(255, 255, 255, 0.2) 0px 1px 0px inset;
  color: #0b2212;
  border: none;
  border-radius: 6px;
  height: 48px;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  margin-top: 5px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background-image: linear-gradient(90deg, #1bca66, #74cc4b);
  }
`;

const StakeSocialButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  min-height: 48px;
  padding: 12px 16px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.05);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const OtpButton = styled.button`
  padding: 0 16px;
  height: 48px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid #2F4553;
  color: white;
  border-radius: 6px;
  margin-left: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  min-width: 110px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const OtpModalStyled = styled(Modal)`
  .modal-dialog {
    max-width: 320px;
    margin: auto;
    margin-top: 15vh;
  }
  .modal-content {
    background-color: #1A2C38;
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
  }
`;

const VerifyButton = styled.button`
  flex: 1;
  padding: 12px;
  background-color: #1fdf71;
  border: none;
  color: #0b2212;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  background: transparent;
  border: 1px solid #2F4553;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: rgba(255, 255, 255, 0.05); }
`;

const StakeDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #9CA3AF;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const SignInButton = styled(Button)`
    color: rgb(36 238 137);
  background:transparent;
  border:none;
`;

const CloseButtonNew = styled.div`
position:absolute;
top:10px;
right:10px;
font-size:18px;
display:none;

@media(max-width:640px){
display:block;
}
`

function RegisterForm({ show: showProp, referral: referralProp }) {
  const [show, setShow] = useState(showProp || false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [referral, setReferral] = useState(referralProp || '');
  const [customFields, setCustomFields] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [effect, setEffect] = useState('zoomIn');
  const [authConfig, setAuthConfig] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState('google');
  const recaptchaRef = React.useRef(null);
  // OTP State
  const [otpSentFor, setOtpSentFor] = useState(null); // 'email' or 'phone'
  const [otpValue, setOtpValue] = useState("");
  const [verifiedFields, setVerifiedFields] = useState({ email: false, phone: false });
  const [sendingOtp, setSendingOtp] = useState(false);

  const { mutateAsync: verifyReferral } = useVerifyReferralMutation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('social_register') === 'true') {
      setEmail(params.get('email') || '');
      setUsername(params.get('name') || '');
      setCustomFields(prev => ({
        ...prev,
        google_id: params.get('google_id'),
        picture: params.get('picture')
      }));
      if (params.get('ref')) {
        setReferral(params.get('ref'));
      }
    }

    const code = params.get('code') || params.get('ref') || params.get('c') || params.get('referral');
    if (code) {
      setReferral(code);
    }

    // Only open automatically if there's a social register or referral code, and only do it once
    if (!window.hasOpenedRegister && (params.get('social_register') === 'true' || code)) {
      window.hasOpenedRegister = true;
      setTimeout(() => {
        setShow(true);
        setEffect('zoomIn');
      }, 500);
    }
    // Cleanup URL
    // window.history.replaceState({}, document.title, window.location.pathname); // Not cleaning up URL to preserve code for other needs

    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API}/api/auth/requirements?type=register`);
        const json = await res.json();
        if (json.status && json.data) {
          setAuthConfig(json.data.fields_config || json.data);
        }
      } catch (err) { }
    };
    fetchConfig();

    socket.on(C.REGISTER_USER, (data) => setRegister(decode(data)));
    Event.on('register_form', () => {
      setShow(true);
      setEffect('zoomIn');
    });
    return () => {
      socket.off(C.REGISTER_USER);
    };
  }, []);

  const setRegister = (data) => {
    console.log(data);
    if (data.error) {
      setDisabled(false);
      setSubmitted(false);
      return sendNotfication(data.error, 'error', 'top-right');
    }
    if (data.status) {
      console.log("datataaaa", data);
      setSubmitted(false);
      socket.emit(C.LOGIN_USER, encode({
        phone: data.phone,
        username: data.name,
        password: data.password,
        recaptcha: 'google'
      }));
      handleClose();
    }
  };

  const handleClose = () => {
    setShow(false);
    setEffect('zoomOut');
    setOtpValue('');
    setVerifiedFields({ email: false, phone: false });
    setDisabled(false);
    setSubmitted(false);
    setCustomFields({});
  };

  const login = () => {
    handleClose();
    Event.emit('login_form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("=== START REGISTER SUBMIT ===");
    console.log("Fields:", { username, email, phone, password, customFields });

    setSubmitted(true);
    setDisabled(true);

    if (!username) {
      sendNotfication('Please enter a username', 'error', 'bottom-left');
      setDisabled(false); setSubmitted(false); return;
    }
    if (authConfig) {
      if (authConfig.email?.required && !email) {
        setDisabled(false); setSubmitted(false); return sendNotfication('Email is required', 'error', 'bottom-left');
      }
      if (authConfig.phone?.required && !phone) {
        setDisabled(false); setSubmitted(false); return sendNotfication('Phone number is required', 'error', 'bottom-left');
      }
      if (authConfig.password?.required && !password) {
        setDisabled(false); setSubmitted(false); return sendNotfication('Password is required', 'error', 'bottom-left');
      }
      if (authConfig.custom_fields) {
        for (let cf of authConfig.custom_fields) {
          if (cf.required && !customFields[cf.id]) {
            setDisabled(false); setSubmitted(false); return sendNotfication(`${cf.label} is required`, 'error', 'bottom-left');
          }
        }
      }
    } else {
      if (!password) {
        setDisabled(false); setSubmitted(false); return sendNotfication('Password is required', 'error', 'bottom-left');
      }
    }

    if (username.length < 5) {
      setDisabled(false);
      setSubmitted(false);
      sendNotfication('Please enter username more than 5 characters', 'error', 'bottom-left');
      return;
    }

    if (authConfig?.referral?.required && !referral) {
      setDisabled(false); setSubmitted(false); return sendNotfication('Referral Code is required.', 'warning');
    }

    if (referral && referral.trim() !== "") {
      try {
        const result = await verifyReferral(referral.trim());
        if (!result?.valid) {
          setDisabled(false);
          setSubmitted(false);
          if (result?.reason === 'affiliate_blocked') {
            sendNotfication('This referral code is not active or has been suspended.', 'error', 'bottom-left');
          } else {
            sendNotfication('Please enter a valid referral code', 'error', 'bottom-left');
          }
          return;
        }
      } catch (err) {
        console.log("referral verify error", err);
        setDisabled(false);
        setSubmitted(false);
        sendNotfication('Could not verify referral code', 'error', 'bottom-left');
        return;
      }
    }

    let minLen = 6;
    let reqUpper = false;
    let reqNum = false;
    let reqSpec = false;

    if (authConfig && authConfig.password) {
      minLen = authConfig.password.minLength || 6;
      reqUpper = authConfig.password.requireUppercase || false;
      reqNum = authConfig.password.requireNumbers || false;
      reqSpec = authConfig.password.requireSpecial || false;
    }

    if (password.length < minLen) {
      setDisabled(false); setSubmitted(false); return sendNotfication(`Password must be at least ${minLen} characters long.`, 'warning');
    }
    if (reqUpper && !/[A-Z]/.test(password)) {
      setDisabled(false); setSubmitted(false); return sendNotfication('Password must contain at least one uppercase letter.', 'warning');
    }
    if (reqNum && !/[0-9]/.test(password)) {
      setDisabled(false); setSubmitted(false); return sendNotfication('Password must contain at least one number.', 'warning');
    }
    if (reqSpec && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      setDisabled(false); setSubmitted(false); return sendNotfication('Password must contain at least one special character.', 'warning');
    }


    // OTP Validation Check
    const emailVerifyRequired = authConfig?.email?.verify === 'register' && authConfig?.email?.visible !== false;
    const phoneVerifyRequired = authConfig?.phone?.verify === 'register' && authConfig?.phone?.visible !== false;

    if (emailVerifyRequired && !verifiedFields.email) {
      setDisabled(false); setSubmitted(false); return sendNotfication('Please verify your Email OTP first.', 'warning');
    }
    if (phoneVerifyRequired && !verifiedFields.phone) {
      setDisabled(false); setSubmitted(false); return sendNotfication('Please verify your Phone OTP first.', 'warning');
    }

    if (authConfig?.recaptcha?.visible && (!recaptchaToken || recaptchaToken === 'google')) {
      console.log("Failed recaptcha validation");
      setDisabled(false); setSubmitted(false); return sendNotfication('Please solve the reCAPTCHA.', 'warning');
    }

    console.log("Validation passed! Emitting REGISTER_USER over socket...");
    socket.emit(C.REGISTER_USER, encode({
      phone: phone && phone.trim() ? phone.trim() : null,
      username: username,
      password: password,
      email: email || "dummyemail@gmail.com",
      method: true,
      refree: referral || '',
      aff: storage.getKey('aff') ? storage.getKey('aff') : null,
      google_id: customFields.google_id,
      picture: customFields.picture,
      custom_fields: customFields,
      recaptcha: recaptchaToken
    }));
    console.log("Emit successful, waiting for server response...");
  };

  const handleSendOtp = async (type, value) => {
    if (!value) return sendNotfication(`Please enter your ${type} first.`, 'warning');
    setSendingOtp(type);
    try {
      const endpoint = type === 'email' ? '/api/users/send-otp' : '/api/users/send-phone-otp';
      const payload = type === 'email' ? { email: value } : { phone: value };

      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok) {
        setOtpSentFor(type);
        sendNotfication(`OTP sent successfully to your ${type}.`, 'success');
      } else {
        sendNotfication(data.message || 'Failed to send OTP', 'error');
      }
    } catch (error) {
      sendNotfication('Error connecting to server', 'error');
    }
    setSendingOtp(false);
  };

  const handleVerifyOtp = async () => {
    if (!otpValue) return sendNotfication('Please enter the OTP.', 'warning');

    try {
      const endpoint = otpSentFor === 'email' ? '/api/users/verify-email-otp' : '/api/users/verify-phone-otp';
      const payload = otpSentFor === 'email' ? { email, otp: otpValue } : { phone, otp: otpValue };

      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!data.status && !response.ok) {
        return sendNotfication(data.message || 'Invalid OTP.', 'error');
      }
      setVerifiedFields(prev => ({ ...prev, [otpSentFor]: true }));
      setOtpSentFor(null);
      sendNotfication('OTP verified successfully.', 'success');
    } catch (error) {
      sendNotfication('Error verifying OTP.', 'error');
    }
  };

  if (storage.getKey('logged')) return null;

  return (
    <>
      <button onClick={() => { setShow(true); setEffect('zoomIn'); }} className='button-brand game-fonts'>
        Join Now
      </button>

      <StyledModal
        size="md"
        centered
        show={show}
        onHide={handleClose}
        aria-labelledby="register-modal"
        className={`animated ${effect}`}
      >
        <ModalBody>
          <CloseButton onClick={handleClose}>×</CloseButton>
          <div style={{ marginBottom: '24px' }}>
            <img src={window?.siteConfig?.logo_big ? (API + window.siteConfig.logo_big) : "/assets/images/logo.png"} alt="Logo" style={{ height: '36px', objectFit: 'contain' }} />
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: '20px 0 0 0', color: 'white' }}>Create an Account</h2>
          </div>

          <StyledForm onSubmit={handleSubmit}>                <FieldContainer>
            <FieldLabel>Username {(!authConfig || authConfig.username?.required !== false) && <span style={{ color: '#ef4444' }}>*</span>}</FieldLabel>
            <StyledInput
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
            />
          </FieldContainer>

            {(() => {
              let fields = [];
              if (!authConfig) {
                fields = [
                  { id: 'email', type: 'email', label: 'Email', required: true, order: 1 },
                  { id: 'password', type: 'password', label: 'Password', required: true, order: 3 }
                ];
              } else {
                if (authConfig.email?.visible !== false) fields.push({ id: 'email', type: 'email', label: 'Email', required: authConfig.email?.required, ...authConfig.email });
                if (authConfig.phone?.visible) fields.push({ id: 'phone', type: 'tel', label: 'Phone', required: authConfig.phone?.required, ...authConfig.phone });
                if (authConfig.password?.visible !== false) fields.push({ id: 'password', type: 'password', label: 'Password', required: authConfig.password?.required, ...authConfig.password });
                if (authConfig.custom_fields) {
                  authConfig.custom_fields.filter(cf => cf.visible !== false).forEach(cf => fields.push(cf));
                }
              }

              return fields.sort((a, b) => (a.order || 10) - (b.order || 10)).map((cf, idx) => {
                if (cf.id === 'email') {
                  return (
                    <FieldContainer key={cf.id}>
                      <FieldLabel>{cf.label} {cf.required && <span style={{ color: '#ef4444' }}>*</span>}</FieldLabel>
                      <InputContainer>
                        <StyledInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" disabled={verifiedFields.email} style={verifiedFields.email ? { borderColor: '#1fdf71', color: '#1fdf71' } : {}} />
                        {cf.verify === 'register' && !verifiedFields.email && (
                          <OtpButton type="button" onClick={() => handleSendOtp('email', email)} disabled={sendingOtp === 'email'}>
                            {sendingOtp === 'email' ? 'Sending...' : 'Send OTP'}
                          </OtpButton>
                        )}
                        {cf.verify === 'register' && verifiedFields.email && (
                          <span style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#1fdf71', display: 'flex', alignItems: 'center' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                          </span>
                        )}
                      </InputContainer>
                    </FieldContainer>
                  );
                }
                if (cf.id === 'phone') {
                  return (
                    <FieldContainer key={cf.id}>
                      <FieldLabel>{cf.label} {cf.required && <span style={{ color: '#ef4444' }}>*</span>}</FieldLabel>
                      <InputContainer>
                        <StyledInput type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone Number" disabled={verifiedFields.phone} style={verifiedFields.phone ? { borderColor: '#1fdf71', color: '#1fdf71' } : {}} />
                        {cf.verify === 'register' && !verifiedFields.phone && (
                          <OtpButton type="button" onClick={() => handleSendOtp('phone', phone)} disabled={sendingOtp === 'phone'}>
                            {sendingOtp === 'phone' ? 'Sending...' : 'Send OTP'}
                          </OtpButton>
                        )}
                        {cf.verify === 'register' && verifiedFields.phone && (
                          <span style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', color: '#1fdf71', display: 'flex', alignItems: 'center' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                          </span>
                        )}
                      </InputContainer>
                    </FieldContainer>
                  );
                }
                if (cf.id === 'password') {
                  return (
                    <FieldContainer key={cf.id}>
                      <FieldLabel>{cf.label} {cf.required && <span style={{ color: '#ef4444' }}>*</span>}</FieldLabel>
                      <InputContainer>
                        <StyledInput 
                          type={showPassword ? "text" : "password"} 
                          value={password} 
                          onChange={(e) => setPassword(e.target.value)} 
                          placeholder="Password" 
                          style={{ paddingRight: '48px' }}
                        />
                        <span 
                          onClick={() => setShowPassword(!showPassword)} 
                          style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#9CA3AF', display: 'flex', alignItems: 'center' }}
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </span>
                      </InputContainer>
                    </FieldContainer>
                  );
                }

                if (cf.type === 'checkbox') {
                  return (
                    <div key={cf.id} style={{ display: 'flex', alignItems: 'flex-start', marginTop: '10px', marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        id={`cf_${cf.id}`}
                        checked={customFields[cf.id] || false}
                        onChange={(e) => setCustomFields({ ...customFields, [cf.id]: e.target.checked })}
                        style={{ marginTop: '4px', accentColor: '#3bca64' }}
                      />
                      <label className='checkbox-signup' htmlFor={`cf_${cf.id}`} style={{ marginLeft: '10px', color: '#9CA3AF', fontSize: '13px' }}>
                        {cf.label}{' '}
                        {cf.required && <span style={{ color: '#ef4444' }}>*</span>}
                        {cf.link_text && cf.link_url && (
                          <a href={cf.link_url} target="_blank" rel="noopener noreferrer" style={{ color: '#83EA77', marginLeft: '4px' }}>
                            {cf.link_text}
                          </a>
                        )}
                      </label>
                    </div>
                  );
                } else {
                  return (
                    <FieldContainer key={cf.id}>
                      <FieldLabel>{cf.label} {cf.required && <span style={{ color: '#ef4444' }}>*</span>}</FieldLabel>
                      <StyledInput
                        type={cf.type === 'number' ? 'number' : cf.type === 'date' ? 'date' : 'text'}
                        value={customFields[cf.id] || ''}
                        onChange={(e) => setCustomFields({ ...customFields, [cf.id]: e.target.value })}
                        placeholder={cf.placeholder || cf.label}
                        style={cf.type === 'date' ? { color: customFields[cf.id] ? 'white' : '#9CA3AF' } : {}}
                      />
                    </FieldContainer>
                  );
                }
              });
            })()}

            {(!authConfig || authConfig.referral?.visible !== false) && (
              <FieldContainer>
                <FieldLabel>
                  Referral Code {authConfig?.referral?.required ? <span style={{ color: '#ef4444' }}>*</span> : '(Optional)'}
                </FieldLabel>
                <StyledInput
                  value={referral}
                  onChange={(e) => setReferral(e.target.value)}
                  placeholder="Code"
                />
              </FieldContainer>
            )}

            {!authConfig?.custom_fields?.length && (
              <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '10px', marginBottom: '10px' }}>
                <input
                  type="checkbox"
                  id="ruleCheck"
                  checked={customFields['ruleCheck18'] || false}
                  onChange={(e) => setCustomFields({ ...customFields, ['ruleCheck18']: e.target.checked })}
                  style={{ marginTop: '4px', accentColor: '#3bca64' }}
                />
                <label className='checkbox-signup' htmlFor="ruleCheck" style={{ marginLeft: '10px', color: '#9CA3AF', fontSize: '13px' }}>
                  I agree & confirm I am at least 18 years old <span style={{ color: '#ef4444' }}>*</span>
                </label>
              </div>
            )}

            {authConfig?.recaptcha?.visible && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px', marginBottom: '10px' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA}
                  theme="dark"
                  onChange={(token) => setRecaptchaToken(token)}
                />
              </div>
            )}

            <SignUpButton type="submit" disabled={disabled}>
              Register
            </SignUpButton>

            {(!authConfig || authConfig.google?.visible || authConfig.facebook?.visible || authConfig.kick?.visible !== false) && (
              <>
                <StakeDivider>Or continue with</StakeDivider>

                <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                  {(!authConfig || authConfig.google?.visible !== false) && (
                    <StakeSocialButton type="button" onClick={() => {
                      window.location.href = `${API}/api/auth/google`;
                    }}>
                      <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{ width: '18px', height: '18px' }} />
                      Google
                    </StakeSocialButton>
                  )}
                  {(!authConfig || authConfig.facebook?.visible !== false) && (
                    <StakeSocialButton type="button" onClick={() => {
                      window.location.href = `${API}/api/auth/facebook${referral ? '?referralCode=' + referral : ''}`;
                    }}>
                      <img src="https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg" alt="Facebook" style={{ width: '18px', height: '18px' }} />
                      Facebook
                    </StakeSocialButton>
                  )}
                  {(!authConfig || authConfig.kick?.visible !== false) && (
                    <StakeSocialButton type="button" onClick={() => {
                      window.location.href = `${API}/api/auth/kick${referral ? '?referralCode=' + referral : ''}`;
                    }}>
                      <img src="/assets/bcimages/kick.png" alt="Kick" style={{ width: '18px', height: '18px', filter: 'invert(1)' }} />
                      Kick
                    </StakeSocialButton>
                  )}
                </div>
              </>
            )}

            <div style={{ textAlign: 'center', marginTop: '4px', fontSize: '14px', color: '#9CA3AF' }}>
              Already have an account? <span style={{ color: '#1fdf71', cursor: 'pointer', fontWeight: 'bold' }} onClick={() => { handleClose(); setTimeout(() => document.querySelector('.login-btn')?.click(), 100) }}>Sign In</span>
            </div>
          </StyledForm>

        </ModalBody>
      </StyledModal>

      <OtpModalStyled show={!!otpSentFor && !verifiedFields[otpSentFor]} onHide={() => setOtpSentFor(null)} backdrop="static" centered>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'white', marginBottom: '16px', fontWeight: 'bold' }}>Enter OTP</h3>
          <p style={{ color: '#aaa', marginBottom: '24px', fontSize: '14px' }}>
            We have sent a 6-digit OTP to your {otpSentFor}.
          </p>
          <StyledInput
            style={{ marginBottom: '24px', textAlign: 'center', letterSpacing: '8px', fontSize: '20px', fontWeight: 'bold' }}
            type="text"
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value)}
            placeholder="------"
            maxLength={6}
          />
          <div style={{ display: 'flex', gap: '12px' }}>
            <CancelButton type="button" onClick={() => setOtpSentFor(null)}>Cancel</CancelButton>
            <VerifyButton type="button" onClick={handleVerifyOtp}>Verify</VerifyButton>
          </div>
        </div>
      </OtpModalStyled>
    </>
  );
}


export default function Register(props) {
  return <RegisterForm {...props} />;
}

